import { z } from 'zod';
/**
 * A survey definition is stored as a single JSONB document on `surveys.definition`
 * and is immutable once the survey is published: stored answers reference question
 * and option ids, so changing them after the fact would make existing answers
 * uninterpretable. To change a published survey, clone it as a new draft.
 */
const slugId = z
    .string()
    .min(1)
    .max(64)
    .regex(/^[a-zA-Z0-9_-]+$/, 'ids may only contain letters, numbers, underscores and hyphens');
export const questionTypes = ['multiple_choice', 'boolean', 'rating', 'open_text'];
export const questionTypeSchema = z.enum(questionTypes);
const baseQuestionFields = {
    id: slugId,
    prompt: z.string().min(1).max(500),
    helpText: z.string().max(1000).optional(),
    required: z.boolean(),
};
export const choiceOptionSchema = z.object({
    id: slugId,
    label: z.string().min(1).max(200),
});
export const multipleChoiceQuestionSchema = z.object({
    ...baseQuestionFields,
    type: z.literal('multiple_choice'),
    options: z.array(choiceOptionSchema).min(2).max(50),
    allowMultiple: z.boolean().default(false),
});
export const booleanQuestionSchema = z.object({
    ...baseQuestionFields,
    type: z.literal('boolean'),
});
export const ratingQuestionSchema = z
    .object({
    ...baseQuestionFields,
    type: z.literal('rating'),
    min: z.number().int().min(0).max(10),
    max: z.number().int().min(1).max(10),
    minLabel: z.string().max(100).optional(),
    maxLabel: z.string().max(100).optional(),
})
    .refine((q) => q.max > q.min, { message: 'max must be greater than min', path: ['max'] });
export const openTextQuestionSchema = z.object({
    ...baseQuestionFields,
    type: z.literal('open_text'),
    maxLength: z.number().int().min(1).max(10000).default(2000),
});
/** Plain union rather than `discriminatedUnion`: `ratingQuestionSchema` carries a `.refine`. */
export const questionSchema = z.union([
    multipleChoiceQuestionSchema,
    booleanQuestionSchema,
    ratingQuestionSchema,
    openTextQuestionSchema,
]);
/**
 * A titled group of questions, used purely to organise a long survey. Sections never nest,
 * and answers still reference the flat question id — a section is presentation only, so moving
 * a question in or out of one does not change how its answers are stored or aggregated.
 */
export const sectionSchema = z.object({
    kind: z.literal('section'),
    id: slugId,
    title: z.string().min(1).max(200),
    description: z.string().max(2000).optional(),
    questions: z.array(questionSchema).max(100),
});
/** One entry in a survey's ordered content: either a standalone question or a section. */
export const surveyItemSchema = z.union([sectionSchema, questionSchema]);
export function isSection(item) {
    return 'kind' in item && item.kind === 'section';
}
/**
 * How the survey is presented to the respondent. `list` is the classic scroll-through form;
 * `slideshow` shows one question per slide and advances on submit. Purely a display concern —
 * the stored answers are identical either way — so it lives on the definition, not the answers.
 */
export const displayModes = ['list', 'slideshow'];
export const displayModeSchema = z.enum(displayModes);
export const surveyDefinitionSchema = z
    .object({
    version: z.literal(1),
    title: z.string().min(1).max(200),
    description: z.string().max(2000).optional(),
    /**
     * An optional free-text statement shown to the respondent before they start — for example a
     * confidentiality note or participation terms. Informational only: it is displayed alongside
     * the anonymity notice, is not something the respondent ticks, and nothing about it is stored
     * against a response.
     */
    agreement: z.string().max(5000).optional(),
    /**
     * An optional closing message shown on the "thank you" screen after the respondent submits,
     * in place of the default confirmation copy. Like `description`, it is plain presentation text.
     */
    completionMessage: z.string().max(2000).optional(),
    /**
     * When true, the completion screen also tells the respondent how long the survey took them.
     * The duration is measured in the browser from when the survey is first shown to when they
     * submit, and is only ever displayed back to that one respondent — it is never sent to or
     * stored by the server, so it cannot be used to re-identify an anonymous response.
     */
    showCompletionTime: z.boolean().optional(),
    displayMode: displayModeSchema.default('list'),
    /**
     * The survey's ordered content: standalone questions and/or sections. Named `questions`
     * for backward compatibility with definitions authored before sections existed — a plain
     * question array is still a valid value. Use `surveyQuestions()` to iterate every question.
     */
    questions: z.array(surveyItemSchema).min(1).max(100),
})
    .superRefine((definition, ctx) => {
    const seenIds = new Set();
    let questionCount = 0;
    // Question and section ids share one namespace: answers reference the flat question id, so
    // it must be unique across the whole survey regardless of which section holds the question.
    const checkId = (id, path) => {
        if (seenIds.has(id)) {
            ctx.addIssue({ code: 'custom', message: `duplicate id "${id}"`, path });
        }
        seenIds.add(id);
    };
    const checkQuestion = (question, path) => {
        questionCount += 1;
        if (question.type === 'multiple_choice') {
            const seenOptionIds = new Set();
            question.options.forEach((option, optionIndex) => {
                if (seenOptionIds.has(option.id)) {
                    ctx.addIssue({
                        code: 'custom',
                        message: `duplicate option id "${option.id}"`,
                        path: [...path, 'options', optionIndex, 'id'],
                    });
                }
                seenOptionIds.add(option.id);
            });
        }
    };
    definition.questions.forEach((item, index) => {
        checkId(item.id, ['questions', index, 'id']);
        if (isSection(item)) {
            item.questions.forEach((question, questionIndex) => {
                checkId(question.id, ['questions', index, 'questions', questionIndex, 'id']);
                checkQuestion(question, ['questions', index, 'questions', questionIndex]);
            });
        }
        else {
            checkQuestion(item, ['questions', index]);
        }
    });
    // A survey made up only of empty sections has no answerable content.
    if (questionCount === 0) {
        ctx.addIssue({ code: 'custom', message: 'a survey needs at least one question', path: ['questions'] });
    }
});
/** Every question in the survey, flattened out of any sections, in presentation order. */
export function surveyQuestions(definition) {
    return definition.questions.flatMap((item) => (isSection(item) ? item.questions : [item]));
}
export const anonymityModes = ['anonymous', 'opt_in', 'identified'];
export const anonymityModeSchema = z.enum(anonymityModes);
export const surveyStatuses = ['draft', 'published', 'closed'];
export const surveyStatusSchema = z.enum(surveyStatuses);
//# sourceMappingURL=survey.js.map